/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-useless-escape */
import { ColumnDef } from '@tanstack/react-table';
import { Form, Offcanvas } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { useRef, useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import productSearchService from 'service/productSearch';
import { showAlert } from 'store/alert/alert.thunk';
import Button from 'components/base/Button';
import AddSerialNumber from './AddSerialNumber';
import discountService from 'service/discount';

export type UpdateSelection = {
  product_name: string;
  barcode: string;
  price_in: string;
  price: string;
  col: string;
  stock: string;
  buy_date: string;
};

interface UpdateSelectionAmountProps {
  setOpenAmount: (show: boolean) => void;
  changedOrderItems?: any;
  setChangedOrderItems?: any;
  selectedRowId?: any;
  isDiscounter?: any;
}

const UpdateSelectionAmount = ({
  setOpenAmount,
  changedOrderItems,
  setChangedOrderItems,
  selectedRowId,
  isDiscounter
}: UpdateSelectionAmountProps) => {
  const { t, i18n } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch: Dispatch<any> = useDispatch();

  const [rowSingleData, setRowSingleData] = useState<any>({});
  const [value, setValue] = useState('');
  const [isProduct, setIsProduct] = useState(false);
  const [disabledPlusIcons, setDisabledPlusIcons] = useState<{
    [key: string]: boolean;
  }>({});
  const [openSerialNumber, setOpenSerialNumber] = useState(false);

  const { data } = useQuery(
    [
      'GET_PRODUCT',
      selectedRowId?.recipient_pid,
      selectedRowId?.nomenclature_pid
    ],
    () => {
      if (
        selectedRowId?.recipient_pid !== 0 &&
        selectedRowId?.nomenclature_pid !== 0
      ) {
        const res = productSearchService
          .getProductWithAccessByIds(
            selectedRowId?.recipient_pid,
            selectedRowId?.nomenclature_pid,
            {
              offset: 0,
              limit: 100
            }
          )
          .then((res: any) => {
            setIsProduct(res?.is_product ?? false);
            return res?.products;
          });

        return res;
      }
    },
    {
      enabled:
        !!selectedRowId?.recipient_pid && !!selectedRowId?.nomenclature_pid
    }
  );

  const comingResultData: UpdateSelection[] = useMemo(() => {
    const resultData =
      data
        ?.filter((item: any) => !!item?.quantity)
        ?.map((items: any) => {
          const packageQuantity =
            items?.package_quantity !== undefined ? items.package_quantity : 1;
          const quantity =
            items?.quantity !== undefined ? String(items.quantity) : '0';

          let integerPart = 0;
          let numerator = 0;
          let denominator = 1;

          if (quantity?.includes('.')) {
            [numerator, denominator] = quantity.split('.').map(Number);
            integerPart = numerator; // Whole number part
            denominator = +`0.${denominator}`; // Remainder of the division
          } else {
            integerPart = +quantity;
          }

          const quantityDisplay = quantity?.includes('.')
            ? `${integerPart}/${Math.round(+denominator * packageQuantity)}`
            : integerPart;

          return {
            nomenclature_id: items?.nomenclature?.id,
            nomenclature_pid: items?.nomenclature?.pid || 0,
            package_quantity: items?.package_quantity,
            product_name:
              isProduct === true ? items?.name : items?.nomenclature?.name,
            barcode:
              isProduct === true
                ? items?.barcode
                : items?.nomenclature?.barcode,
            /* prettier-ignore */
            price: 
             items?.price??0,
            /* prettier-ignore */
            price_in:  items?.price_in ?? 0,

            /* prettier-ignore */
            price_whosale: items?.price_whosale ?? 0,

            /* prettier-ignore */
            col:
              items?.nomenclature?.measure_unit_id === "443bfff1-61e0-4057-8583-d040dc5a0454"
                ? quantityDisplay
                : items?.quantity,
            measure_unit_id: items?.nomenclature?.measure_unit_id,
            stock: items?.warehouse?.name?.[i18n?.language],
            product_id: items?.id,
            product_pid: items?.pid || 0,
            product_parent_id: items?.id,
            product_parent_pid: items?.pid || 0,
            warehouse_id: items?.warehouse_id,
            warehouse_pid: items?.warehouse_pid || 0,
            buy_date: new Date(items?.date_created)
              .toLocaleDateString('en-GB')
              .replace(/\//g, '-'),
            serial_number: items?.partion_codes,
            partion_codes_map: items?.partion_codes_map
          };
        }) ?? [];

    return resultData;
  }, [data, t, i18n?.language]);

  const handlePlusIconClick = (rowId: any) => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setDisabledPlusIcons(() => {
      const newDisabledPlusIcons: { [key: string]: boolean } = {};
      newDisabledPlusIcons[rowId] = true;
      return newDisabledPlusIcons;
    });
  };

  const updateSelectionTableColumns: ColumnDef<UpdateSelection>[] = [
    {
      id: 'action',
      cell: rowData => (
        <div>
          <Button variant="hover">
            <FeatherIcon
              icon={disabledPlusIcons[rowData.row.id] ? 'check' : 'plus'}
              className="cursor-pointer"
              size={22}
              onClick={() => {
                handlePlusIconClick(rowData.row.id);
                setRowSingleData(rowData?.row?.original);
              }}
            />
          </Button>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'price_in',
      header: t('purchase_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price_whosale',
      header: t('wholesale_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'price',
      header: t('selling_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'col',
      header: t('short_quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'stock',
      header: t('warehouse'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'buy_date',
      header: 'Дата закупа',
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const handleChange = (e: any) => {
    const newValue = e.target.value;
    const measure_unit_id = rowSingleData?.measure_unit_id;

    // Pattern for integers (e.g., "5", "-5")
    const integerPattern = /^-?\d*$/;
    // Pattern for fractions (e.g., "5/2", "-5/2", allows single slash)
    const fractionPattern = /^-?\d*\/?\d*$/;
    // Pattern for decimal numbers (e.g., "123.45", "-123.45", allows . or ,)
    const decimalPattern = /^-?\d*[.,]?\d*$/;

    // Allow empty string to clear input
    if (!newValue) {
      setValue('');
      return;
    }

    let isPatternValid = false;
    let numValue: number | undefined;

    if (measure_unit_id === '0830abaa-87e7-49cc-a3cf-f8a49834e34d') {
      // Only allow integers, no /, ., or ,
      if (
        integerPattern.test(newValue) &&
        !newValue.includes('.') &&
        !newValue.includes(',') &&
        !newValue.includes('/')
      ) {
        isPatternValid = true;
        numValue = parseInt(newValue, 10);
      }
    } else if (measure_unit_id === '443bfff1-61e0-4057-8583-d040dc5a0454') {
      // Only allow fraction pattern (with /), no . or ,
      if (
        fractionPattern.test(newValue) &&
        !newValue.includes('.') &&
        !newValue.includes(',')
      ) {
        isPatternValid = true;
        const [numerator] = newValue.split('/');
        numValue = parseInt(numerator || newValue, 10);
      }
    } else {
      // Allow decimal numbers (with . or ,)
      if (decimalPattern.test(newValue)) {
        isPatternValid = true;
        numValue = parseFloat(newValue.replace(',', '.'));
      }
    }

    // Update state if pattern is valid, even if range check fails
    if (isPatternValid) {
      setValue(newValue);
      // Show alert only if range check fails
      if (
        numValue === undefined ||
        isNaN(numValue) ||
        numValue <= 0 ||
        numValue > +rowSingleData?.col
      ) {
        dispatch(
          showAlert({
            title: t('select_product_add_quantity')
          })
        );
      }
    }
  };

  const handleKeyDown = (e: any) => {
    const measure_unit_id = rowSingleData?.measure_unit_id;
    // Allow specific keys based on measure_unit_id
    if (measure_unit_id === '0830abaa-87e7-49cc-a3cf-f8a49834e34d') {
      // Integers: allow numbers, backspace, delete, arrows, minus
      if (
        !/[0-9]/.test(e.key) &&
        e.key !== 'Backspace' &&
        e.key !== 'Delete' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight' &&
        e.key !== '-'
      ) {
        e.preventDefault();
      }
    } else if (measure_unit_id === '443bfff1-61e0-4057-8583-d040dc5a0454') {
      // Fractions: allow numbers, /, backspace, delete, arrows, minus
      if (
        !/[0-9/]/.test(e.key) &&
        e.key !== 'Backspace' &&
        e.key !== 'Delete' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight' &&
        e.key !== '-'
      ) {
        e.preventDefault();
      }
    } else {
      // Decimals: allow numbers, ., ,, backspace, delete, arrows, minus
      if (
        !/[0-9.,]/.test(e.key) &&
        e.key !== 'Backspace' &&
        e.key !== 'Delete' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight' &&
        e.key !== '-'
      ) {
        e.preventDefault();
      }
    }
  };

  const handleAccept = async () => {
    if (isDiscounter) {
      if (value) {
        const quantity = value !== undefined ? String(value) : '0';

        let integerPart = 0;
        let numerator = 0;
        let denominator = 1; // Default denominator to 1 if no fraction

        const quantityStr = String(quantity);
        // Check if quantity contains '/'
        if (quantityStr?.includes('/')) {
          [numerator, denominator] = quantityStr.split('/').map(Number);

          integerPart = numerator; // Whole number part
          // eslint-disable-next-line
          denominator = denominator; // Remainder of the division
        } else {
          integerPart = +quantityStr;
        }

        // Package quantity
        const packageQuantity = +rowSingleData?.package_quantity || 1;
        // Calculate the fractional proportion
        const fractionalProportion = (denominator * 100) / packageQuantity;

        // Calculate adjusted quantity
        const adjustedQuantity = quantityStr?.includes('/')
          ? `${integerPart}.${Math.round(fractionalProportion)}`
          : integerPart;

        /* prettier-ignore */
        const requestValue =
          rowSingleData?.measure_unit_id ===
          '443bfff1-61e0-4057-8583-d040dc5a0454'
            ? adjustedQuantity
            : +value;

        try {
          const resData = await discountService
            ?.getListCheckDiscountItem(selectedRowId.barcode, requestValue)
            .then((res: any) => res);

          const percentage = resData?.percentage ?? 0;
          const discount_id = resData?.id ?? '';
          const discount_pid = resData?.pid || 0;

          const updatedData = changedOrderItems.map((item: any, index: any) => {
            const discountedCost =
              percentage > 0
                ? +rowSingleData?.price_whosale * (1 - percentage / 100)
                : +rowSingleData?.price_whosale;
            if (index === +selectedRowId.rowId) {
              return {
                ...item,
                warehouse_id: rowSingleData?.warehouse_id,
                warehouse_pid: rowSingleData?.warehouse_pid || 0,
                product_id: rowSingleData?.product_id,
                product_pid: rowSingleData?.product_pid || 0,
                product_parent_id: rowSingleData?.product_parent_id,
                product_parent_pid: rowSingleData?.product_parent_pid || 0,
                quantity_fact: value,
                package_quantity: rowSingleData?.package_quantity,
                price: rowSingleData?.price,
                price_in: rowSingleData?.price_in,
                // price_whosale: rowSingleData?.price_whosale,
                nomenclature_id: rowSingleData?.nomenclature_id,
                nomenclature_pid: rowSingleData?.nomenclature_pid || 0,
                discount_percentage: percentage,
                cost: discountedCost,
                prevCost: +rowSingleData?.price_whosale,
                update_price: percentage > 0 ? true : false,
                discount_id: discount_id,
                discount_pid: discount_pid || 0
              };
            }
            return item;
          });

          setChangedOrderItems(updatedData);
          setOpenAmount(false);
        } catch (error) {
          console.log('error==>', error);
        }
      } else {
        dispatch(
          showAlert({
            title: t('select_product_add_quantity')
          })
        );
      }
    } else {
      if (value) {
        const updatedData = changedOrderItems.map((item: any, index: any) => {
          if (index === +selectedRowId.rowId) {
            return {
              ...item,
              warehouse_id: rowSingleData?.warehouse_id,
              warehouse_pid: rowSingleData?.warehouse_pid || 0,
              product_id: rowSingleData?.product_id,
              product_pid: rowSingleData?.product_pid || 0,
              product_parent_id: rowSingleData?.product_parent_id,
              product_parent_pid: rowSingleData?.product_parent_pid || 0,
              quantity_fact: value,
              price: rowSingleData?.price,
              price_in: rowSingleData?.price_in,
              package_quantity: rowSingleData?.package_quantity,
              // price_whosale: rowSingleData?.price_whosale,
              nomenclature_id: rowSingleData?.nomenclature_id,
              nomenclature_pid: rowSingleData?.nomenclature_pid || 0,
              discount_percentage: 0,
              prevCost: +rowSingleData?.price_whosale,
              update_price: false
            };
          }
          return item;
        });

        setChangedOrderItems(updatedData);
        setOpenAmount(false);
      } else {
        dispatch(
          showAlert({
            title: t('select_product_add_quantity')
          })
        );
      }
    }
  };

  const table = useAdvanceTable({
    data: comingResultData,
    columns: updateSelectionTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
        <AdvanceTableFooter pagination />

        <div className="d-flex justify-content-end align-items-center text-center p-2 gap-3 border-top border-200">
          <div className="d-flex gap-3 align-items-center bg-light p-2">
            <p className="mb-0 text-bold">{t('order_quantity')}:</p>
            <span>{selectedRowId?.quantity_asked}</span>
          </div>

          <Form.Floating>
            <Form.Control
              ref={inputRef}
              value={value}
              type="text"
              placeholder={t('select_quantity')}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={e => e.target.select()}
            />
            <label htmlFor="floatingInputCustom">{t('select_quantity')}</label>
          </Form.Floating>

          <Button
            variant="primary"
            onClick={() => {
              if (Object.keys(rowSingleData)?.length > 0) {
                if (value && rowSingleData?.serial_number?.length > 0) {
                  setOpenSerialNumber(true);
                } else {
                  handleAccept();
                }
              } else {
                dispatch(
                  showAlert({
                    title: t('select_product_add_quantity')
                  })
                );
              }
            }}
          >
            {t('apply')}
          </Button>
        </div>
      </AdvanceTableProvider>
      <Offcanvas
        show={openSerialNumber}
        onHide={() => {
          setOpenSerialNumber(false);
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
        style={{ zIndex: 1060 }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('serial_number')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <AddSerialNumber
            quantityTime={value}
            selectedRow={rowSingleData}
            setOpenSerialNumber={setOpenSerialNumber}
            isDiscounter={isDiscounter}
            changedOrderItems={changedOrderItems}
            setOpenAmount={setOpenAmount}
            setChangedOrderItems={setChangedOrderItems}
            selectedRowId={selectedRowId}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default UpdateSelectionAmount;
